import { Button, Modal } from '@/components/basic';
import { LoadingSpinner } from '@/components/util/LoadingSpinner';
import { api } from '@/lib/api/api';
import { IResourcePermission } from '@gettactic/api';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

type Props = {
  onClose: () => void;
  permissions: IResourcePermission[];
  resourceIds: string[];
};

export const ToggleIsApprovablePermission = ({
  onClose,
  permissions,
  resourceIds
}: Props) => {
  const queryClient = useQueryClient();
  const { handleSubmit } = useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const onToggleSubmit = async () => {
    setLoading(true);

    let toggled = 0;
    let error = 0;
    for (const permission of permissions) {
      const res =
        await api.client.organizations.resourceScheduledPermissionsUpdate(
          {
            ...permission,
            since: permission.since ?? undefined,
            is_approvable: !permission.is_approvable
          },
          permission.resource_id,
          permission.id,
          permission.team?.id,
          permission.user?.id
        );
      if (res.error) {
        error++;
      } else {
        toggled++;
      }
    }
    setLoading(false);
    queryClient.invalidateQueries([
      'organizations.resourceScheduledPermissionsAll'
    ]);
    if (error > 0) {
      toast.error("Sorry we couldn't change all the permission");
    } else {
      toast.success(
        toggled === 1 ? 'Assignment updated' : 'Group of assignments updated'
      );
      onClose();
    }
  };

  return (
    <form
      id="toggleIsApprovablePermission"
      onSubmit={handleSubmit(onToggleSubmit)}
    >
      <div>
        <div className="mt-4">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg
                className="h-6 w-6 text-red-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <p className="text-base text-gray-800">
                Are you sure you want to toggle “Requires Approval” for
                <strong className="font-bold">
                  {permissions.length === 1
                    ? ' this assignment'
                    : ' this group of assignments'}
                </strong>
                ?
              </p>
            </div>
          </div>
        </div>
      </div>
      <Modal.Footer>
        <Button
          className="w-full sm:w-auto"
          layout="squared"
          variant="white"
          font="boldGray"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          className="w-full sm:ml-3 sm:w-auto"
          layout="squared"
          variant="primary"
          type="submit"
          form="toggleIsApprovablePermission"
          disabled={loading}
        >
          {loading ? (
            <LoadingSpinner color="text-white" />
          ) : (
            <span>Update</span>
          )}
        </Button>
      </Modal.Footer>
    </form>
  );
};

export default ToggleIsApprovablePermission;
